import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import CancellationsApi from '../../../api/CancellationsApi';
import { handleUnauthorizedResponse } from '../../../api/http';
import PolicyApi from '../../../api/PolicyApi';
import { AuthData } from '../../../context/auth';
import {
	RenewalData,
	withRenewalContext,
} from '../../../context/RenewalProvider';
import { Exposure, ExposureCreateRequest } from '../../../types/policy';
import {
	StyledSectionContainer,
	StyledTitle,
	StyledTitleContainer,
} from '../../common';
import { errorToast, successToast } from '../../Toast';
import ConfirmationModal from '../ConfirmationModal';
import AddExposureModal from './Exposures/AddExposureModal';
import ExposuresList from './Exposures/ExposuresList';
import { getExposuresForRenewal } from './util';

interface Props {
	authData: AuthData;
	renewalData: RenewalData;
}

/**
 * The exposure list on an renewal.
 */
const Exposures = (props: Props) => {
	const { t } = useTranslation();
	const { getValues, formState, reset } = useFormContext();
	const { dirtyFields } = formState;

	const [isConfirmModalOpen, setIsConfirmModalOpen] = React.useState(false);
	const [removedExposure, setRemoveExposure] = React.useState<
		Exposure | ExposureCreateRequest
	>(null);
	const { authData, renewalData } = props;

	const { renewalUpdateObj, productConfig, policy, setLoading, saveFastForm } =
		renewalData;

	const { exposures } = policy;

	const [graceLapseReinstatementData, setGraceLapse] = useState([]);
	const [cancellations, setCancellations] = useState([]);

	useEffect(() => {
		const fetchCancellationsAndGraceLapse = async () => {
			setLoading(true);
			try {
				const cancellationResponse =
					await CancellationsApi.getCancellationsForPolicy(
						authData.auth.authorizationToken,
						policy.locator,
					);
				if (cancellationResponse) {
					setCancellations(cancellationResponse.data);
				}
				const graceLapse = await PolicyApi.getGraceLapseAndReinstatementInfo(
					authData.auth.authorizationToken,
					policy.locator,
				);
				if (graceLapse) {
					setGraceLapse(graceLapse.data);
				}
			} catch (err) {
				console.error(err);
				setLoading(false);
			}

			setLoading(false);
		};
		fetchCancellationsAndGraceLapse();
	}, []);

	// only show exposures that were active/present prior to the effective date of the renewal
	const renewalEffectiveDate = renewalUpdateObj.renewalStartTimestamp;
	const activeExposuresAtTimeOfRenewal = getExposuresForRenewal(
		renewalEffectiveDate,
		policy,
		exposures,
		cancellations,
		graceLapseReinstatementData,
	);

	const policyConfig = productConfig.policyConfiguration;

	const removeExposure = async () => {
		setIsConfirmModalOpen(false);
		setLoading(true);
		try {
			// Determine if this exposure is added, create options object accordingly
			const removeExposureReq =
				'displayId' in removedExposure
					? {
							removeExposure: { exposureLocator: removedExposure.locator },
					  }
					: {
							removeAddedExposure: { exposureLocator: removedExposure.locator },
					  };
			await saveFastForm(getValues(), dirtyFields, reset, removeExposureReq);
			successToast('Successfully removed exposure');
		} catch (error) {
			errorToast('Error removing exposure');
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
		} finally {
			setLoading(false);
		}
	};

	const renderActions = () => {
		return (
			<AddExposureModal
				policyConfig={policyConfig}
				authData={authData}
				renewalData={renewalData}
			/>
		);
	};

	const renderRemoveExposureModal = () => {
		return (
			<ConfirmationModal
				entityName={
					(removedExposure as Exposure).name ||
					(removedExposure as ExposureCreateRequest).exposureName
				}
				entityType={t('exposure.singular.lower')}
				authToken={authData.auth.authorizationToken}
				onRemove={() => removeExposure()}
				cancel={() => setIsConfirmModalOpen(false)}
			/>
		);
	};

	const openConfirmationModal = (exposure: Exposure) => {
		if (exposure) {
			setIsConfirmModalOpen(true);
			setRemoveExposure(exposure);
		}
	};

	const hasWritePermissions =
		authData.hasPermission('renewal.update') &&
		renewalUpdateObj.state === 'application';
	return (
		<>
			<StyledTitleContainer>
				<StyledTitle>{t('exposure.plural.upper')}</StyledTitle>
				{isConfirmModalOpen && renderRemoveExposureModal()}
			</StyledTitleContainer>
			<hr />
			<StyledSectionContainer>
				<ExposuresList
					exposures={activeExposuresAtTimeOfRenewal}
					openConfirmationModal={openConfirmationModal}
				/>
			</StyledSectionContainer>
			{hasWritePermissions && renderActions()}
		</>
	);
};

export default withRenewalContext<Props>(Exposures);
